import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import { Formik } from 'formik';
import { Alert } from '@lightspeed/flame/Alert';
import { Button } from '@lightspeed/flame/Button';
import Text from '@lightspeed/cirrus-text';

import Layout from 'cirrus/Layout';
import get from 'lodash/get';

import { entities } from 'pages/store/schema';
import { getData, putData, clearData } from 'pages/store/actions/data';
import { getProcessing, getValues, getErrors } from 'pages/store/selectors';
import { actionTypes } from 'pages/store/actions/defaults';

import { formatForAPI, formatForForm } from './utils/format';
import FeaturesSection from './Features';

const merchantos = window.merchantos;

class OptionalModules extends Component {
  constructor(props) {
    super(props);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.state = {
      saved: false,
    };
  }

  componentDidMount() {
    this.props.getData('/admin/settings/customer_modules');

    merchantos.asyncSubscribe.notifySubscribers('react.mount.topbar', {
      props: {
        crumbs: [
          {
            type: 'ui_tab',
            text: 'Settings',
            name: 'admin',
          },
          {
            text: 'Optional Modules',
          },
        ],
      },
    });
  }

  componentWillUnmount() {
    this.props.clearData();
  }

  handleSubmit(values, { resetForm }) {
    this.props.putData(
      '/admin/settings/customer_modules',
      formatForAPI(values, 'customer_modules'),
      () => {
        // This makes sure that the form data is synced with the returned API data
        resetForm();
        this.setState({ saved: true });
      }
    );
  }

  render() {
    const { modules, isLoading, loadingErrors, isSaving, savingErrors } = this.props;
    const { saved } = this.state;
    const hasErrors = !!(loadingErrors || savingErrors);
    const initialValues = formatForForm(modules);

    return (
      <div className="cirrus cr-sans-serif" data-test="settings-optional-modules-root">
        <Formik
          enableReinitialize
          initialValues={initialValues}
          onSubmit={this.handleSubmit}
          render={formikProps => {
            const { handleSubmit, dirty } = formikProps;

            return (
              <Fragment>
                <header className="cr-bg-snow-300 cr-p-2 cr-border-bottom-1 cr-border-gray-100">
                  <div className="cr-max-width-content cr-display-block-centered">
                    <Button
                      variant="secondary"
                      fill
                      loading={isSaving}
                      disabled={isLoading || !dirty}
                      onClick={handleSubmit}
                      data-test="settings-optional-modules-save-button"
                    >
                      Save Changes
                    </Button>
                  </div>
                </header>

                {hasErrors && (
                  <div className="cr-p-2">
                    <div
                      className="cr-max-width-content cr-display-block-centered"
                      data-test="settings-optional-modules-alert"
                    >
                      <Alert type="danger" noCloseBtn>
                        {get(
                          savingErrors,
                          'response.data.errors[0].detail',
                          'An error has occurred. Please reload the page and try again'
                        )}
                      </Alert>
                    </div>
                  </div>
                )}

                {saved && !hasErrors && !isSaving && (
                  <div className="cr-p-2">
                    <div
                      className="cr-max-width-content cr-display-block-centered"
                      data-test="settings-optional-modules-alert"
                    >
                      <Alert type="warning" noCloseBtn>
                        <Text>Saved changes.</Text>
                        <Text>
                          To activate this setting, all employees currently logged in must log out
                          and back in again.
                        </Text>
                      </Alert>
                    </div>
                  </div>
                )}

                <div data-test="settings-optional-modules-features">
                  <Layout>
                    <FeaturesSection isLoading={isLoading} modules={modules} />
                  </Layout>
                </div>
              </Fragment>
            );
          }}
        />
      </div>
    );
  }
}

const mapStateToProps = state => ({
  modules: getValues(entities.customerModules)(state),
  isLoading: getProcessing({
    entity: entities.customerModules,
    list: true,
    actionType: actionTypes.LOADING,
  })(state),
  isSaving: getProcessing({
    entity: entities.customerModules,
    list: true,
    actionType: actionTypes.SAVING,
  })(state),
  loadingErrors: getErrors({
    entity: entities.customerModules,
    list: true,
    actionType: actionTypes.LOADING,
  })(state),
  savingErrors: getErrors({
    entity: entities.customerModules,
    list: true,
    actionType: actionTypes.SAVING,
  })(state),
});

const mapDispatchToProps = dispatch => ({
  getData: (url, onError) =>
    dispatch(getData({ url, entity: entities.customerModules, list: true, onError })),
  putData: (url, data, onSuccess) =>
    dispatch(
      putData({ url, payload: data, entity: entities.customerModules, list: true, onSuccess })
    ),
  clearData: () => dispatch(clearData([entities.customerModules])),
});

const ConnectedOptionalModules = connect(mapStateToProps, mapDispatchToProps)(OptionalModules);
export default ConnectedOptionalModules;
